import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Users, BookOpen, Calendar, ChevronRight, Quote, Clock, MapPin, Database, Gift, Megaphone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  heroContent,
  impactStats,
  programs,
  upcomingEvents,
  testimonials,
  organizationInfo
} from '../data/mock';

const HomePage = () => {
  const iconMap = {
    BookOpen: BookOpen,
    Heart: Heart,
    Users: Users,
    Database: Database,
    Gift: Gift,
    Megaphone: Megaphone,
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        {/* Background Image with Overlay */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${heroContent.backgroundImage})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-[#2D2A32]/70 via-[#2D2A32]/50 to-[#2D2A32]/80" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-20">
          <div className="animate-fade-in-up">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              {heroContent.title}
              <span className="block text-[#E07A5F] mt-2">{heroContent.subtitle}</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-200 max-w-3xl mx-auto mb-10 leading-relaxed">
              {heroContent.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-[#E07A5F] hover:bg-[#C96A50] text-white rounded-full px-8 py-6 text-lg transition-all duration-300 hover:shadow-xl hover:scale-105"
              >
                <Link to="/resources">
                  {heroContent.ctaPrimary}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#2D2A32] rounded-full px-8 py-6 text-lg transition-all duration-300 bg-transparent"
              >
                <Link to="/get-involved">
                  <Heart className="mr-2 w-5 h-5" />
                  {heroContent.ctaSecondary}
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/70 rounded-full mt-2 animate-scroll" />
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="bg-[#5D4E6D] py-16 relative -mt-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {impactStats.map((stat) => (
              <div
                key={stat.label}
                className="text-center group"
              >
                <div className="text-4xl sm:text-5xl font-bold text-white mb-2 transition-transform duration-300 group-hover:scale-110">
                  {stat.number}
                </div>
                <div className="text-[#E07A5F] font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 bg-[#F5F0EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider">Our Mission</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2D2A32] mt-4 mb-6 leading-tight">
                Supporting Hawaii's Cancer Community with Aloha
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                {organizationInfo.mission}
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                We believe in a holistic approach to cancer care, connecting patients and families with 
                traditional medicine, alternative treatments, nutritional support, and mental health resources. 
                Our programs are designed to educate, inspire, and provide tangible support to those in need.
              </p>
              <Button
                asChild
                className="bg-[#5D4E6D] hover:bg-[#4A3D58] text-white rounded-full px-6 transition-all duration-300"
              >
                <Link to="/about">
                  Learn More About Us
                  <ChevronRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1527525443983-6e60c75fff46?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxoYW5kcyUyMHRvZ2V0aGVyfGVufDB8fHx8MTc2OTE0ODgxN3ww&ixlib=rb-4.1.0&q=85"
                  alt="Community support"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Decorative Elements */}
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-[#E07A5F]/20 rounded-full blur-2xl" />
              <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-[#5D4E6D]/20 rounded-full blur-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Programs Preview */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider">What We Do</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2D2A32] mt-4">
              Our Programs
            </h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Comprehensive support services designed to help cancer patients and caregivers thrive.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {programs.map((program) => {
              const IconComponent = iconMap[program.icon] || Heart;
              return (
                <Card
                  key={program.id}
                  className="group border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 overflow-hidden"
                >
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-[#E07A5F]/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#E07A5F] transition-colors duration-300">
                      <IconComponent className="w-7 h-7 text-[#E07A5F] group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="text-xl font-bold text-[#2D2A32] mb-3">{program.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {program.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Button
              asChild
              variant="outline"
              className="border-2 border-[#5D4E6D] text-[#5D4E6D] hover:bg-[#5D4E6D] hover:text-white rounded-full px-8 transition-all duration-300"
            >
              <Link to="/programs">
                View All Programs
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-[#5D4E6D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider">Testimonials</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-4">
              Stories of Hope
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 relative group hover:bg-white/15 transition-colors duration-300"
              >
                <Quote className="w-10 h-10 text-[#E07A5F] mb-4" />
                <p className="text-white/90 text-lg leading-relaxed mb-6">
                  "{testimonial.quote}"
                </p>
                <div>
                  <p className="text-white font-semibold">{testimonial.author}</p>
                  <p className="text-[#E07A5F] text-sm">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Events Preview */}
      <section className="py-24 bg-[#F5F0EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16">
            <div>
              <span className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider">Join Us</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2D2A32] mt-4">
                Upcoming Events
              </h2>
            </div>
            <Button
              asChild
              variant="outline"
              className="mt-6 md:mt-0 border-2 border-[#5D4E6D] text-[#5D4E6D] hover:bg-[#5D4E6D] hover:text-white rounded-full px-6 transition-all duration-300"
            >
              <Link to="/events">
                View All Events
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>

          <div className="space-y-8">
            {upcomingEvents.map((event) => (
              <Card
                key={event.id}
                className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
              >
                <div className="grid md:grid-cols-3">
                  <div className="aspect-square md:aspect-auto overflow-hidden">
                    <img
                      src={event.image}
                      alt={event.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="md:col-span-2 p-8">
                    <div className="flex flex-wrap items-center gap-4 mb-4">
                      <Badge className="bg-[#E07A5F]/10 text-[#E07A5F] hover:bg-[#E07A5F]/20">
                        {event.type}
                      </Badge>
                    </div>
                    <h3 className="text-2xl font-bold text-[#2D2A32] mb-4">{event.title}</h3>
                    <p className="text-gray-600 mb-6">{event.description}</p>
                    <div className="grid sm:grid-cols-3 gap-4 mb-6">
                      <div className="flex items-center text-gray-600">
                        <Calendar className="w-5 h-5 mr-2 text-[#5D4E6D]" />
                        {event.date}
                      </div>
                      <div className="flex items-center text-gray-600">
                        <Clock className="w-5 h-5 mr-2 text-[#5D4E6D]" />
                        {event.time}
                      </div>
                      <div className="flex items-center text-gray-600">
                        <MapPin className="w-5 h-5 mr-2 text-[#5D4E6D]" />
                        {event.location}
                      </div>
                    </div>

                    {event.qrCode && (
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 p-5 mb-6 bg-[#F5F0EB] rounded-2xl border border-[#E07A5F]/20">
                        <div className="w-32 h-32 flex-shrink-0 bg-white p-2 rounded-xl shadow-md">
                          <img
                            src={event.qrCode}
                            alt="Scan QR code for tickets"
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <div>
                          <p className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider mb-1">
                            Tickets On Sale Now
                          </p>
                          <h4 className="text-xl font-bold text-[#2D2A32] mb-2">
                            Scan to Get Your Tickets
                          </h4>
                          <p className="text-gray-600 text-sm">
                            Scan the QR code with your phone camera, or tap the button below to purchase tickets online.
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="flex flex-col sm:flex-row flex-wrap gap-4">
                      {event.link && (
                        <Button
                          asChild
                          data-testid={`home-event-tickets-btn-${event.id}`}
                          className="bg-[#E07A5F] hover:bg-[#C96A50] text-white rounded-full px-6 transition-all duration-300"
                        >
                          <a href={event.link} target="_blank" rel="noopener noreferrer">
                            Get Tickets
                            <ArrowRight className="ml-2 w-4 h-4" />
                          </a>
                        </Button>
                      )}
                      <Button
                        asChild
                        data-testid={`home-event-volunteer-btn-${event.id}`}
                        variant="outline"
                        className="border-2 border-[#E07A5F] text-[#E07A5F] hover:bg-[#E07A5F] hover:text-white rounded-full px-6 transition-all duration-300"
                      >
                        <Link to="/events#festival-volunteer">
                          Volunteer & Earn 2 Tickets
                        </Link>
                      </Button>
                      <Button
                        asChild
                        variant="outline"
                        className="border-2 border-[#5D4E6D] text-[#5D4E6D] hover:bg-[#5D4E6D] hover:text-white rounded-full px-6 transition-all duration-300"
                      >
                        <Link to="/events">Learn More</Link>
                      </Button>
                    </div>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#2D2A32] relative overflow-hidden">
        {/* Background Decoration */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#E07A5F]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#5D4E6D]/20 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Together, We Can Make a Difference
          </h2>
          <p className="text-gray-300 text-lg mb-10 max-w-2xl mx-auto">
            Join our ohana and help support Hawaii's cancer community. Every contribution, 
            big or small, makes a lasting impact.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-[#E07A5F] hover:bg-[#C96A50] text-white rounded-full px-10 py-6 text-lg transition-all duration-300 hover:shadow-xl"
            >
              <Link to="/get-involved">
                <Heart className="mr-2 w-5 h-5" />
                Donate Now
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#2D2A32] rounded-full px-10 py-6 text-lg transition-all duration-300 bg-transparent"
            >
              <Link to="/get-involved">Become a Volunteer</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
